import {
  OreCheckbox,
  OreFormGroup,
  OreHeading,
  OreInput,
  OreSelect,
  OreSelectPlaceholder,
  OreStack,
} from '@edenredespana/oreneta'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AutoEnrollmentTranslation } from '@/AutoEnrollment/ui/autoEnrollmentTranslation'
import { Step2Form } from '@/AutoEnrollment/ui/hooks'
import { handleErrors } from '@/Shared/ui/form'
import { FoodTypeModel } from '@/Store/domain'

interface Step2GeneralStoreDataProps {
  foodTypes: FoodTypeModel[]
}

export const Step2GeneralStoreData = ({
  foodTypes,
}: Step2GeneralStoreDataProps): JSX.Element => {
  const { t } = useTranslation()

  const { register, formState, resetField, getValues, setError, clearErrors, setValue } =
    useFormContext<Step2Form>()
  const { errors } = handleErrors(formState)

  const { onChange: onChangeOnline, ...restOnline } = register('storeIsOnlineStore')
  const { onChange: onChangeDelivery, ...restDelivery } = register('storeHasHomeDelivery')
  const { onChange: onChangeTakeAway, ...restTakeAway } = register('storeHasLocalPickup')

  const { onChange: onChangeFoodType, ...restFoodType } = register('storeFoodTypeId')

  const serviceError = (): void => {
    if (
      !getValues('storeIsOnlineStore') &&
      !getValues('storeHasHomeDelivery') &&
      !getValues('storeHasLocalPickup')
    ) {
      setError('service', {
        type: 'manual',
        message: t(AutoEnrollmentTranslation.storeGeneralData.data.service.error),
      })
    } else {
      clearErrors('service')
    }
  }

  const setFoodTypeName = (target: HTMLSelectElement): void => {
    if (target.value !== null) {
      setValue(
        'storeFoodType',
        foodTypes.find(f => f.id?.toString() === target.value)?.name ?? '',
      )
    }
  }

  useEffect(() => {
    resetField('storeFoodTypeId')
  }, [foodTypes])

  return (
    <OreStack placeContent="stretch" space="large">
      <OreHeading as="h2" size="headline-md">
        {t(AutoEnrollmentTranslation.storeGeneralData.title)}
      </OreHeading>
      <OreStack direction="row" space="large" placeContent="stretch">
        <OreInput
          {...register('storeName')}
          {...errors('storeName')}
          id="storeName"
          label={t(AutoEnrollmentTranslation.storeGeneralData.data.storeName.label)}
          placeholder={t(
            AutoEnrollmentTranslation.storeGeneralData.data.storeName.placeholder,
          )}
          labelTooltip={t(
            AutoEnrollmentTranslation.storeGeneralData.data.storeName.tooltip,
          )}
          required
          maxLength={255}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('storeEmail')}
          {...errors('storeEmail')}
          id="storeEmail"
          label={t(AutoEnrollmentTranslation.storeGeneralData.data.storeEmail.label)}
          type="email"
          placeholder={t(
            AutoEnrollmentTranslation.storeGeneralData.data.storeEmail.placeholder,
          )}
          required
          maxLength={255}
        />
        <OreInput
          {...register('storePhone')}
          {...errors('storePhone')}
          id="storePhone"
          label={t(AutoEnrollmentTranslation.storeGeneralData.data.storePhone.label)}
          type="tel"
          placeholder={t(
            AutoEnrollmentTranslation.storeGeneralData.data.storePhone.placeholder,
          )}
          labelTooltip={t(
            AutoEnrollmentTranslation.storeGeneralData.data.storePhone.tooltip,
          )}
          required
          maxLength={255}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('storeWeb')}
          {...errors('storeWeb')}
          id="storeWeb"
          label={t(AutoEnrollmentTranslation.storeGeneralData.data.storeWeb.label)}
          placeholder={t(
            AutoEnrollmentTranslation.storeGeneralData.data.storeWeb.placeholder,
          )}
          labelTooltip={t(
            AutoEnrollmentTranslation.storeGeneralData.data.storeWeb.tooltip,
          )}
          legend={t(AutoEnrollmentTranslation.storeGeneralData.data.storeWeb.legend)}
          maxLength={255}
        />
        <OreSelect
          {...errors('storeFoodTypeId')}
          {...restFoodType}
          id="storeFoodTypeId"
          label={t(AutoEnrollmentTranslation.storeGeneralData.data.foodType.label)}
          required
          labelTooltip={t(
            AutoEnrollmentTranslation.storeGeneralData.data.foodType.tooltip,
          )}
          onChange={(e): void => {
            setFoodTypeName(e.target)
            onChangeFoodType(e)
          }}
        >
          <OreSelectPlaceholder
            label={t(
              AutoEnrollmentTranslation.storeGeneralData.data.foodType.placeholder,
            )}
          />
          {foodTypes
            ? foodTypes.map(
                foodType =>
                  foodType.id && (
                    <option key={foodType.id} value={foodType.id}>
                      {foodType.name}
                    </option>
                  ),
              )
            : null}
        </OreSelect>
      </OreStack>

      <OreFormGroup
        label={t(AutoEnrollmentTranslation.storeGeneralData.data.service.label)}
        required
        direction="row"
        {...errors('service')}
      >
        <OreCheckbox
          {...restOnline}
          {...errors('storeIsOnlineStore')}
          label={t(
            AutoEnrollmentTranslation.storeGeneralData.data.service.types.physical,
          )}
          onChange={(e): void => {
            onChangeOnline(e)
            serviceError()
          }}
        />
        <OreCheckbox
          {...restDelivery}
          {...errors('storeHasHomeDelivery')}
          label={t(
            AutoEnrollmentTranslation.storeGeneralData.data.service.types.delivery,
          )}
          onChange={(e): void => {
            onChangeDelivery(e)
            serviceError()
          }}
        />
        <OreCheckbox
          {...restTakeAway}
          {...errors('storeHasLocalPickup')}
          label={t(
            AutoEnrollmentTranslation.storeGeneralData.data.service.types.takeAway,
          )}
          onChange={(e): void => {
            onChangeTakeAway(e)
            serviceError()
          }}
        />
      </OreFormGroup>
    </OreStack>
  )
}
